import React from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import styled from 'styled-components'
import { canDrag, percent, WON_STAGE } from 'utility/crm/deal-board'
import { formatMoney } from 'utility/crm/dates'
import { DealCard } from './DealCard'
import { Muted } from './ui'

const Scroller = styled.div`
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 8px;
    align-items: flex-start;
    -webkit-overflow-scrolling: touch;
`

const Column = styled.section`
    flex: 1 0 250px;
    min-width: 250px;
    border-radius: 10px;
    background: rgba(128, 128, 128, 0.1);
    padding: 10px;
    .head {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 2px;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }
`

const DropZone = styled.div`
    min-height: 60px;
    margin-top: 8px;
    border-radius: 8px;
    background: ${({ $over }) => ($over ? 'rgba(37,99,235,.12)' : 'transparent')};
    transition: background 0.1s;
`

const AddButton = styled.button`
    width: 100%;
    background: none;
    border: 1px dashed rgba(128, 128, 128, 0.5);
    border-radius: 8px;
    padding: 6px;
    color: inherit;
    cursor: pointer;
    opacity: 0.8;
    &:hover {
        opacity: 1;
    }
`

/**
 * Kanban board. Cards move between stages by drag and drop (keyboard: focus the card, Space to lift,
 * arrows to move) and, as the always-available alternative, with "Move to stage" in the deal drawer.
 * @param {{
 *   columns: {Stage: string, Count: number, Amount: number, Probability: number}[],
 *   groups: Record<string, import('utility/crm/types').CrmDeal[]>,
 *   dealsById: Record<number, import('utility/crm/types').CrmDeal>,
 *   onDrop: (deal: import('utility/crm/types').CrmDeal, toStage: string) => void,
 *   onOpen: (deal: import('utility/crm/types').CrmDeal) => void,
 *   onAdd: (stage: string) => void,
 * }} props
 */
export const DealBoard = ({ columns, groups, dealsById, onDrop, onOpen, onAdd }) => (
    <DragDropContext
        onDragEnd={result => {
            if (!result.destination) return
            const deal = dealsById[Number(result.draggableId)]
            if (deal) onDrop(deal, result.destination.droppableId)
        }}
    >
        <Scroller role="group" aria-label="Pipeline board">
            {columns.map(col => {
                const deals = groups[col.Stage] || []
                return (
                    <Column key={col.Stage} aria-label={`${col.Stage}, ${deals.length} deals`}>
                        <div className="head">
                            <span>{col.Stage}</span>
                            <span>{deals.length}</span>
                        </div>
                        <Muted>
                            {formatMoney(col.Amount)} · {percent(col.Probability)} win
                        </Muted>
                        <Droppable droppableId={col.Stage}>
                            {(provided, snapshot) => (
                                <DropZone ref={provided.innerRef} {...provided.droppableProps} $over={snapshot.isDraggingOver}>
                                    {deals.map((deal, index) => (
                                        <Draggable key={deal.DealId} draggableId={String(deal.DealId)} index={index} isDragDisabled={!canDrag(deal)}>
                                            {(drag, dragSnapshot) => (
                                                <DealCard
                                                    deal={deal}
                                                    onOpen={onOpen}
                                                    dragging={dragSnapshot.isDragging}
                                                    innerRef={drag.innerRef}
                                                    {...drag.draggableProps}
                                                    {...drag.dragHandleProps}
                                                />
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </DropZone>
                            )}
                        </Droppable>
                        {col.Stage !== WON_STAGE ? (
                            <AddButton type="button" onClick={() => onAdd(col.Stage)} aria-label={`Add deal to ${col.Stage}`}>
                                + Add deal
                            </AddButton>
                        ) : null}
                    </Column>
                )
            })}
        </Scroller>
        <Muted style={{ display: 'block', marginTop: 6 }}>
            Drag a card to another stage, or open it to change stage from the keyboard. Moving to Won starts the customer handoff.
        </Muted>
    </DragDropContext>
)
